import { useCallback, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { shallowEqual, useDispatch, useSelector, useStore } from 'react-redux';
import { BlockType, IDIncomeVerificationStatus, VerificationType } from '@common/entities';
import { useMutation } from '@tanstack/react-query';
import { VerificationFieldState } from 'state/slices/idIncomeVerificationSlice';
import { updateLeasingJourneyErrors } from 'state/slices/navigationSlice';
import { RootState } from 'state/store';

import { processVerificationStatusUpdate } from 'components/render-page/pages/summary-page/verification/utils';
import { useGetActiveStepIndex } from 'hooks/useGetActiveStepIndex';
import { useGetPageData } from 'hooks/useGetPageData';
import { SaveResponseProps, useSaveResponse } from 'hooks/useSaveResponse';
import { buildPageRequestData } from 'utils/buildPageRequest';
import { verificationErrorCount } from 'utils/getErrors';
import handleError from 'utils/handleError';

import { useCheckVerificationStatus } from './useCheckVerificationStatus';
import { useFetchVerificationStatus } from './useFetchVerificationStatus';
import useHandleVerificationFailedWorkFlow from './useHandleVerificationFailedWorkFlow';
import { useVerificationUtils } from './useVerificationUtils';

function normalizeVerificationStatus(
  status: IDIncomeVerificationStatus | null,
): IDIncomeVerificationStatus {
  return status ?? IDIncomeVerificationStatus.NotStarted;
}

function fieldHasVerificationErrors(field: VerificationFieldState): boolean {
  return verificationErrorCount(field) > 0;
}

export const useCheckVerificationStatusOnSummaryPage = () => {
  const [isLoading, setIsLoading] = useState(false);

  const dispatch = useDispatch();
  const store = useStore<RootState>();
  const { setValue } = useFormContext();

  const { application, idIncomeVerification } = useSelector(
    (state: RootState) => ({
      application: state.application,
      idIncomeVerification: state.idIncomeVerification,
    }),
    shallowEqual,
  );

  const { form, activeResponse, applicant } = application;
  const { pages } = form;
  const { applicationId } = activeResponse;
  const { propertyId } = applicant;

  const { idVerification, incomeVerification } = idIncomeVerification;
  const { status: idVerificationStatus } = idVerification;
  const { status: incomeVerificationStatus } = incomeVerification;

  const getActiveStepIndex = useGetActiveStepIndex();
  const getPageResponses = useGetPageData();
  const saveResponse = useSaveResponse();
  const { mutateAsync: updateResponse } = useMutation({
    mutationFn: (data: SaveResponseProps) => saveResponse(data),
  });

  const { showButton, countdown, isButtonDisabled, checkStatus, showCheckButton } =
    useCheckVerificationStatus();
  const { showVerificationCards, shouldWeFetchVerificationStatus } = useVerificationUtils();
  const { showIdVerificationCard, showIncomeVerificationCard } = showVerificationCards;

  const { fetchVerificationStatus } = useFetchVerificationStatus();
  const { handleVerificationFailedWorkFlow } = useHandleVerificationFailedWorkFlow();

  const updateStepErrorCount = useCallback(
    async (fieldPageIndex: number | null) => {
      if (fieldPageIndex === null) return;

      const { leasingJourneyErrors } = store.getState().navigation;
      const currentErrorCount = leasingJourneyErrors[fieldPageIndex] ?? 0;
      const newErrorCount = Math.max(0, currentErrorCount - 1);

      dispatch(
        updateLeasingJourneyErrors({
          pageIndex: fieldPageIndex,
          count: newErrorCount,
        }),
      );

      const { requestData } = buildPageRequestData({
        activeStepIndex: fieldPageIndex,
        newPageIndex: getActiveStepIndex(),
        pages,
        applicationId,
        propertyId,
        getPageResponses: () => getPageResponses(fieldPageIndex),
        errorCount: newErrorCount,
      });
      await updateResponse(requestData);
    },
    [
      dispatch,
      store,
      getActiveStepIndex,
      pages,
      applicationId,
      propertyId,
      getPageResponses,
      updateResponse,
    ],
  );

  const updateFormContext = useCallback(
    (type: VerificationType, status: IDIncomeVerificationStatus) => {
      const prefix =
        type === VerificationType.IdentityVerification
          ? BlockType.IDVerification
          : BlockType.IncomeVerification;
      setValue(`${prefix}.status`, status);
    },
    [setValue],
  );

  const handleVerificationStatus = useCallback(
    async (
      status: IDIncomeVerificationStatus,
      verificationType: VerificationType,
      fieldPageIndex: number | null,
      shouldUpdateFieldErrorCount: boolean = false,
    ) => {
      if (
        status !== IDIncomeVerificationStatus.Failed &&
        status !== IDIncomeVerificationStatus.Success
      ) {
        return;
      }
      updateFormContext(verificationType, status);
      if (status === IDIncomeVerificationStatus.Failed) {
        handleVerificationFailedWorkFlow(verificationType, status);
      } else if (status === IDIncomeVerificationStatus.Success && shouldUpdateFieldErrorCount) {
        await updateStepErrorCount(fieldPageIndex);
      }
    },
    [handleVerificationFailedWorkFlow, updateFormContext, updateStepErrorCount],
  );

  const checkStatusOnLoad = useCallback(async () => {
    if (!shouldWeFetchVerificationStatus) return;
    setIsLoading(true);
    try {
      const status = await fetchVerificationStatus();

      const branches = [
        {
          newStatus: status?.idVerification?.status,
          currentStatus: idVerificationStatus,
          verificationType: VerificationType.IdentityVerification,
          fieldPageIndex: idVerification.fieldPageIndex ?? 0,
        },
        {
          newStatus: status?.incomeVerification?.status,
          currentStatus: incomeVerificationStatus,
          verificationType: VerificationType.IncomeVerification,
          fieldPageIndex: incomeVerification.fieldPageIndex ?? 0,
        },
      ] as const;

      const inProgressFlags = branches.map((branch) => {
        if (!branch.newStatus) return false;
        const { isInProgress } = processVerificationStatusUpdate({
          newStatus: branch.newStatus,
          currentStatus: branch.currentStatus,
          verificationType: branch.verificationType,
          fieldPageIndex: branch.fieldPageIndex,
          dispatch,
          handleVerificationStatus,
        });
        return isInProgress;
      });

      if (inProgressFlags.some(Boolean)) showCheckButton();
    } catch (error: unknown) {
      handleError('Error checking status:', error instanceof Error ? error.message : String(error));
    } finally {
      setIsLoading(false);
    }
  }, [
    shouldWeFetchVerificationStatus,
    fetchVerificationStatus,
    idVerificationStatus,
    incomeVerificationStatus,
    idVerification.fieldPageIndex,
    incomeVerification.fieldPageIndex,
    dispatch,
    handleVerificationStatus,
    showCheckButton,
  ]);

  const checkVerificationStatusOnSummaryPage = useCallback(async () => {
    setIsLoading(true);
    try {
      const shouldDecrementErrorsForId =
        fieldHasVerificationErrors(idVerification) && showIdVerificationCard;
      const shouldDecrementErrorsForIncome =
        fieldHasVerificationErrors(incomeVerification) && showIncomeVerificationCard;

      await checkStatus();
      const verificationStore = store.getState().idIncomeVerification;

      if (showIdVerificationCard) {
        await handleVerificationStatus(
          normalizeVerificationStatus(verificationStore.idVerification.status),
          VerificationType.IdentityVerification,
          idVerification.fieldPageIndex ?? 0,
          shouldDecrementErrorsForId,
        );
      }
      if (showIncomeVerificationCard) {
        await handleVerificationStatus(
          normalizeVerificationStatus(verificationStore.incomeVerification.status),
          VerificationType.IncomeVerification,
          incomeVerification.fieldPageIndex ?? 0,
          shouldDecrementErrorsForIncome,
        );
      }
    } catch (error: unknown) {
      handleError('Error checking status:', error instanceof Error ? error.message : String(error));
    } finally {
      setIsLoading(false);
    }
  }, [
    checkStatus,
    store,
    showIdVerificationCard,
    showIncomeVerificationCard,
    idVerification,
    incomeVerification,
    handleVerificationStatus,
  ]);

  return {
    showButton,
    countdown,
    isButtonDisabled,
    checkStatusOnLoad,
    checkStatus: checkVerificationStatusOnSummaryPage,
    showCheckButton,
    showVerificationCards,
    shouldWeFetchVerificationStatus,
    isLoading,
  };
};
